import type { ComputedRef, Ref } from 'vue';
import type { LocationQuery } from 'vue-router';
import type {
  InitialData as SmsHistorySearchFormInitialData,
  ValidData as SmsHistorySearchFormValidData,
} from '@/components/searchForms/SmsHistorySearchForm.vue';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { isCancel } from 'axios';
import { format } from 'date-fns';
import _isEqual from 'lodash/isEqual';

import { fetchSMSHistoryData } from '@/helpers/fetchers';
import * as locationQueryHelpers from '@/helpers/locationQuery';

interface SmsHistoryData {
  total: number;
  page: number;
  perPage: number;
  items: openapi.SMSHistoryContentOpenapi['results'];
}

export interface SmsHistoryViewComposable {
  search: (formData: SmsHistorySearchFormValidData) => void;
  initialData: ComputedRef<SmsHistorySearchFormInitialData>;
  isFetchingData: Ref<boolean>;
  smsHistoryData: Ref<SmsHistoryData>;
}

export default function useSmsHistoryView(
  queryCurPage: Readonly<Ref<number>>,
  queryPageSize: Readonly<Ref<number>>,
  queryFromDate: Readonly<Ref<Date | undefined>>,
  queryToDate: Readonly<Ref<Date | undefined>>,
  queryDriver: Readonly<Ref<number | undefined>>,
): SmsHistoryViewComposable {
  const router = useRouter();
  const route = useRoute();

  const initialData = computed<SmsHistorySearchFormInitialData>(() => {
    let fromDate;
    let toDate;
    if (queryFromDate.value) {
      fromDate = format(queryFromDate.value, 'yyyy-MM-dd');
    }
    if (queryToDate.value) {
      toDate = format(queryToDate.value, 'yyyy-MM-dd');
    }
    const driver = queryDriver.value;
    let dateRange: [Date, Date] | undefined;
    if (fromDate && toDate) {
      dateRange = [new Date(fromDate), new Date(toDate)];
    }

    return {
      dateRange,
      driver,
    };
  });

  const isFetchingData = ref(false);

  const smsHistoryData = ref<SmsHistoryData>({
    total: 0,
    page: 1,
    perPage: 10,
    items: [],
  });

  function search(formData: SmsHistorySearchFormValidData): void {
    const query = locationQueryHelpers.buildSmsHistoryLocationQuery(
      route.query,
      formData,
    );
    const isReplace = _isEqual(route.query, query);
    void router.push({
      name: route.name || 'smsHistoryView',
      query,
      force: true,
      replace: isReplace,
    });
  }

  let smsHistoryController: AbortController | undefined;

  async function fetchData(newQuery: LocationQuery): Promise<void> {
    if (smsHistoryController) smsHistoryController.abort();

    const page = queryCurPage.value;
    const perPage = queryPageSize.value;
    const fromDate = queryFromDate.value;
    const toDate = queryToDate.value;
    const driver = queryDriver.value;

    smsHistoryData.value = {
      total: 0,
      page: 1,
      perPage: 10,
      items: [],
    };

    isFetchingData.value = true;

    const params: openapi.SMSHistoryQueryOpenapi = {
      page,
      page_size: perPage,
    };

    if (fromDate) {
      params.fromDate = format(fromDate, 'yyyy-MM-dd');
    }
    if (toDate) {
      params.toDate = format(toDate, 'yyyy-MM-dd');
    }
    if (driver) {
      params.driver = driver.toString();
    }

    smsHistoryController = new AbortController();
    await fetchSMSHistoryData(params, {
      signal: smsHistoryController.signal,
    })
      .then((data: openapi.SMSHistoryContentOpenapi) => {
        if (!_isEqual(route.query, newQuery)) {
          return;
        }
        smsHistoryData.value = {
          total: data.count,
          page,
          perPage,
          items: data.results,
        };
      })
      .catch((err: unknown) => {
        if (isCancel(err)) return;
        throw err;
      })
      .finally(() => {
        isFetchingData.value = false;
      });
  }

  watch(
    route,
    async (newRoute) => {
      await fetchData(newRoute.query);
    },
    { immediate: true },
  );

  return {
    search,
    initialData,
    isFetchingData,
    smsHistoryData,
  };
}
